import { Component, OnInit, OnDestroy, ViewChild, ViewEncapsulation, inject, effect, ChangeDetectorRef } from "@angular/core";
import { Router, RouterOutlet, NavigationEnd } from "@angular/router";
import { filter, Subscription } from "rxjs";
import { HeaderCreateComponent } from "@components/layouts/header-create/header-create.component";
import { SidebarCreateComponent } from "@components/layouts/sidebar-create/sidebar-create.component";
import { FooterCreateComponent } from "@components/layouts/footer-create/footer-create.component";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";
import { Constants } from "@config/constants";
import { HttpService } from "@services/http.service";
import { AuthService } from "@services/auth.service";
import { CreateFlowStateService } from "@services/create-flow-state.service";

@Component({
  selector: "kzn-layout-create",
  standalone: true,
  imports: [RouterOutlet, HeaderCreateComponent, SidebarCreateComponent, FooterCreateComponent, MatIconModule, MatButtonModule],
  providers: [HttpService],
  templateUrl: "./layout-create.component.html",
  styleUrl: "./layout-create.component.scss",
  encapsulation: ViewEncapsulation.None,
})
export class LayoutCreateComponent implements OnInit, OnDestroy {
  @ViewChild("sidebarRef") sidebarRef!: SidebarCreateComponent;

  private _httpService = inject(HttpService);
  private _authService = inject(AuthService);
  private _router = inject(Router);
  private _createFlow = inject(CreateFlowStateService);
  private _cdr = inject(ChangeDetectorRef);
  private _routerSub?: Subscription;
  /** Kept for diagnostic use; layout no longer blocks render on user details. Components subscribe to userDetails$. */
  isGettingUserDetails = false;

  /** Hide KAIZEN nav sidebar on Field step (PDF tools + document editor needs full width). */
  hideNavSidebar = false;

  kapybaraSidebarOpen = false;
  sidebarMobileOpen = false;

  constructor() {
    effect(() => {
      this._createFlow.hideLayoutNavSidebar();
      this.refreshNavSidebarVisibility();
      this._cdr.markForCheck();
    });
  }

  showGettingStarted = false;
  showQuickTipsSection = false;
  showHelpSection = false;
  assistantResponseText = '';
  assistantResponseLoading = false;

  ngOnInit(): void {
    /** Only fetch once per session; cached value already broadcasts via userDetails$ to subscribers. */
    if (!this._authService.getUserDetails()) this.getUserDetails();
    this.refreshNavSidebarVisibility();
    this._routerSub = this._router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => {
        this.closeMobileSidebar();
        this.refreshNavSidebarVisibility();
      });
  }

  private refreshNavSidebarVisibility(): void {
    const path = (this._router.url || "").split("?")[0].split("#")[0];
    const onCreateWizard =
      path.includes("/create/new") ||
      path.includes("/create/bulk") ||
      /^\/edit\/[^/]+$/.test(path);
    this.hideNavSidebar = onCreateWizard && this._createFlow.hideLayoutNavSidebar();
    if (this.hideNavSidebar) {
      this.closeMobileSidebar();
    }
  }

  ngOnDestroy(): void {
    this._routerSub?.unsubscribe();
  }

  private get isMobileView(): boolean {
    return window.innerWidth <= 1024;
  }

  onToggleSidebar(): void {
    if (this.hideNavSidebar) {
      return;
    }
    if (this.isMobileView) {
      this.sidebarMobileOpen = !this.sidebarMobileOpen;
      if (this.sidebarMobileOpen && this.sidebarRef) {
        this.sidebarRef.expanded = true;
      }
    } else {
      this.sidebarRef?.toggle();
    }
  }

  closeMobileSidebar(): void {
    this.sidebarMobileOpen = false;
  }

  onToggleKapybara(): void {
    this.kapybaraSidebarOpen = !this.kapybaraSidebarOpen;
  }

  closeKapybara(): void {
    this.kapybaraSidebarOpen = false;
  }

  handleAssistantAction(action: string): void {
    this.assistantResponseText = '';
    this.assistantResponseLoading = true;

    const responses: Record<string, string> = {
      startTour:
        "Welcome to Kaizen DocSign! Here's a quick tour:\n\n" +
        "1. Dashboard — View your recent activity and pending requests\n" +
        "2. Create Request — Upload a document, add signers, and send for signature\n" +
        "3. Requests — Track all sent and received signing requests\n" +
        "4. Email Templates — Create reusable email templates for notifications\n" +
        "5. Settings — Manage your profile, users, and preferences\n\n" +
        "Click on any menu item in the sidebar to get started!",
      quickTips:
        "Here are some quick tips to get you started:\n\n" +
        "• Use 'Create Request' to upload a PDF and add signature fields\n" +
        "• Add multiple signers with different roles (Signer, Reviewer, Approver)\n" +
        "• Track document status in real-time from the Requests page\n" +
        "• Set up Email Templates to save time on recurring documents\n" +
        "• Use the Settings page to manage users and configure preferences",
    };

    setTimeout(() => {
      this.assistantResponseLoading = false;
      this.assistantResponseText = responses[action] || "I'm here to help! Let me know what you'd like to know.";
    }, 1200);
  }

  private getUserDetails(): void {
    this._httpService
      .get(this._httpService.createEndpoint(Constants.API_URI_USER_DETAILS))
      .subscribe(
        (response: any) => {
          this._authService.setUserDetails(response.Data);
          this.isGettingUserDetails = false;
        },
        (error) => {
          this._httpService.showHttpError(error);
          this._authService.afterLogoutSuccess();
          this.isGettingUserDetails = false;
        }
      );
  }
}
